import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box, Text } from 'grommet';
// eslint-disable-next-line import/no-extraneous-dependencies
import { FileImportType } from '@RedflagAI/node-utility/interface';
import { useAppTheme } from '@Components/Context';
import {
  Seo, AppButton, SimpleModal, FlexibleSelectDropdown,
} from '@Components/Control';
import { AdminNavWrapper } from '@Components/Navigation';
import {
  clearFileImportsRequest,
  initiateFileImportsRequest,
  fetchCompanyListRequest,
} from '@Actions';
import {
  TableWrapper,
  NoDataPlaceholder,
  renderTableHeader,
  SearchInput,
} from '../../../Partial/Product/SharedComponents';
import ImportResultSummary from './ImportResultSummary';


const ImportsHistoryPage = ({
  small,
  location,
  loading,
  fetchFileImports,
  clearFileImports,
  fetchCompanyList,
  fileImports = null,
  companyList = [],
}) => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalConfig, setModalConfig] = React.useState(null);
  const [query, setQuery] = React.useState({
    page: 1,
    pageSize: 25,
    type: null,
    companyId: null,
    name: '',
  });

  const { theme: { page, button }, isDarkMode } = useAppTheme();

  React.useEffect(() => {
    fetchFileImports({ ...query });
    fetchCompanyList();

    return () => {
      clearFileImports();
    };
  }, []);

  React.useEffect(() => {
    fetchFileImports({ ...query });
  }, [query]);

  const handleQuery = (key, value, isFilter) => {
    setQuery((prev) => ({
      ...prev,
      [key]: value,
      ...(isFilter ? { page: 1 } : {}),
    }));
  };

  const resetQuery = () => {
    setQuery({
      page: 1,
      pageSize: 25,
      type: null,
      companyId: null,
      name: '',
    });
  };

  const handleOpenConfigModal = (row) => {
    const config = row.config || {};
    // List of keys to exclude from summary
    const excludeKeys = ['errors', 'fileImportId', 'fileImportName', 'summary'];
    // Build dynamic summary from config
    const dynamicSummary = Object.keys(config)
      .filter((key) => !excludeKeys.includes(key))
      .reduce((acc, key) => {
        acc[key] = config[key];
        return acc;
      }, {});

    const formattedConfig = {
      ...config,
      fileImportId: row.id,
      fileImportName: row.name,
      summary: Object.keys(dynamicSummary).length > 0 ? dynamicSummary : config.summary || {},
      errors: config.errors || [],
    };
    setModalConfig(formattedConfig);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalConfig(null);
  };

  // --- Dropdown options ---
  const typeOptions = Object.values(FileImportType).map((type) => ({
    api: type,
    display: type.charAt(0).toUpperCase() + type.slice(1),
  }));

  const companyOptions = companyList?.map(({ id, name }) => ({
    api: id,
    display: name,
  }));

  const totalResults = fileImports?.total;
  const noResults = Boolean(fileImports && fileImports?.results?.length === 0);
  const padding = small ? '1.5rem' : '2rem';
  const usePageBg = isDarkMode ? page.background : page.altBg;

  const TABLE_HEADER_MAP = [
    { uid: 'id', display: 'Import ID', size: '12%' },
    { uid: 'name', display: 'Name', size: '22%' },
    { uid: 'type', display: 'Type', size: '28%' },
    { uid: 'companyId', display: 'Company ID', size: '18%' },
    { uid: 'config', display: 'Config', size: '20%' },
  ];

  const tableControls = (
    <Box direction="row" gap="1rem">
      <SearchInput
        key="search"
        customPlaceholder="Search by name"
        small={small}
        handleSearch={(_, value) => handleQuery('name', value, true)}
        initialValue={query.name || ''}
      />
      <FlexibleSelectDropdown
        textColor={page.primaryText}
        handleItemSelect={(value) => handleQuery('type', value, true)}
        flexibleOptions={typeOptions}
        noSelectionLabel="All Types"
        displayKey="display"
        valueKey="api"
        selectedOption={
          typeOptions.find((opt) => opt.api === query.type)?.display || 'All Types'
        }
      />
      {companyOptions?.length > 0 && (
        <FlexibleSelectDropdown
          textColor={page.primaryText}
          handleItemSelect={(value) => handleQuery('companyId', value, true)}
          flexibleOptions={companyOptions}
          noSelectionLabel="All Companies"
          displayKey="display"
          valueKey="api"
          selectedOption={
            companyOptions?.find((opt) => opt.api === query.companyId)?.display || 'All Companies'
          }
        />
      )}
      <AppButton
        overrideHover
        disabled={query.type === null && query.companyId === null && query.name === ''}
        onClick={() => resetQuery()}
        level="tableControls"
        color={button.primaryText}
        bgColor={button.primaryBg}
        label="Reset"
      />
    </Box>
  );

  const renderRowItem = (uid, item) => {
    if (uid === 'config' && item.config) {
      return (
        <AppButton
          overrideHover
          level="tableControls"
          bgColor={button.primaryBg}
          color={button.primaryText}
          label="Summary"
          onClick={() => handleOpenConfigModal(item)}
        />
      );
    }

    return (
      <Text size="0.875rem" color={page.primaryText}>
        {item[uid] ?? 'N/A'}
      </Text>
    );
  };

  const renderTableContents = () => {
    if (loading || noResults) {
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          showRefreshButton={!loading && noResults}
          refreshQuery={resetQuery}
          buttonHighlight={button.primaryBg}
          loading={loading}
          label="No results matching these filters"
          textColor={page.primaryText}
          loaderColor={page.highlight}
        />
      );
    }

    return (
      <Box fill="horizontal" direction="column">
        {!small && (
          <Box
            fill="horizontal"
            wrap={false}
            direction="row"
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
            }}
          >
            {TABLE_HEADER_MAP.map(({ uid, display, size }) => (
              <Box key={uid} width={size} pad={{ horizontal: '0.5rem', top: '1rem', bottom: '0.5rem' }}>
                {renderTableHeader(display, small)}
              </Box>
            ))}
          </Box>
        )}
        {fileImports?.results?.map((dt) => (
          <Box
            pad={{ vertical: '0.5rem' }}
            key={dt.id}
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
            }}
            direction={small ? 'column' : 'row'}
            fill="horizontal"
            align={small ? 'start' : 'center'}
          >
            {TABLE_HEADER_MAP.map(({ uid, size }) => (
              <Box
                key={uid}
                width={small ? '100%' : size}
                pad={{ left: small ? '0' : '0.5rem', vertical: small ? '0.25rem' : '0' }}
                direction="row"
                gap="0.5rem"
              >
                {small && (
                  <Text size="0.875rem" color={page.tableHeader}>
                    {TABLE_HEADER_MAP.find((h) => h.uid === uid).display}
                    :
                  </Text>
                )}
                {renderRowItem(uid, dt)}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <AdminNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'View file import history and statuses',
        subTitle: 'File Imports',
      }}
    >
      <Seo />
      <Box flex background={usePageBg} pad={{ vertical: padding }}>
        <TableWrapper
          small={small}
          header="Imports History"
          textColor={page.primaryText}
          boxPad="1.5rem"
          handleResultsPerPage={(size) => handleQuery('pageSize', size)}
          handlePage={(newPage) => handleQuery('page', newPage)}
          resultsPerPageOptions={[25, 50, 75]}
          selectedResultsCount={query.pageSize}
          hidePagination={!fileImports || noResults || loading}
          currentPage={fileImports ? fileImports.page : 1}
          currentResults={fileImports?.results?.length}
          isPaged={totalResults > fileImports?.results?.length}
          totalResults={totalResults}
          tableControls={tableControls}
        >
          {renderTableContents()}
        </TableWrapper>
        {modalOpen && (
          <SimpleModal
            small={small}
            modalTitle="Import Summary"
            toggleModal={handleCloseModal}
            highlightColor={page.highlight}
            titleSize="1.25rem"
          >
            <ImportResultSummary
              hideHistoryLink
              importResult={modalConfig}
              textColor={page.modaltext}
              clearImportResult={handleCloseModal}
            />
          </SimpleModal>
        )}
      </Box>
    </AdminNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    loading: state.fetchLoader.dataLoading,
    fileImports: state.adminFileImports.fileImports,
    companyList: state.adminCompany.companies,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    clearFileImports: clearFileImportsRequest,
    fetchFileImports: initiateFileImportsRequest,
    fetchCompanyList: fetchCompanyListRequest,
  }, dispatch);
}

ImportsHistoryPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  fetchCompanyList: PropTypes.func.isRequired,
  companyList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })),
  fetchFileImports: PropTypes.func.isRequired,
  clearFileImports: PropTypes.func.isRequired,
  fileImports: PropTypes.shape({
    page: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    results: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      companyId: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      createdAt: PropTypes.string.isRequired,
      config: PropTypes.object,
    })),
  }),
};

export default connect(mapStateToProps, mapDispatchToProps)(ImportsHistoryPage);
